<i18n>
{
  "no": {
    "errors": {
      "expired": "Økten har utløpt. Lukk nettleser og forsøk igjen.",
      "ninMismatch": "Vi fant deg ikke i folkeregisteret. Kontakt kundeservice.",
      "signUpLocked": "Noe gikk galt. Lukk nettleser og prøv igjen.",
      "invalidForm": "Alle påkrevde felter må fylles ut.",
      "badState": "Vi kunne ikke fullføre registrering av konto. Vennligst kontakt kundeservice for videre hjelp.",
      "tryAgain": "Vennligst prøv på nytt",
      "contactCustomerService": "Kontakt kundeservice"
    },
    "submit": "Bekreft identitet",
    "continueWithoutSignUp": "Fortsett uten fødselsnummer",
    "kyc": {
      "header": "Bekreft din identitet"
    },
    "description": "I henhold til norsk lov må vi verifisere din identitet når du bruker våre tjenester. Om du f.eks. gjør en endring på et abonnement eller handler i nettbutikken, skal du være trygg på at sikkerheten din blir ivaretatt."
  },
  "en": {
    "errors": {
      "expired": "This session has expired. Please close this browser window and try again.",
      "ninMismatch": "We can't find you in the National Registry. Please contact customer service.",
      "signUpLocked": "Something went wrong. Please close this browser window and try again.",
      "invalidForm": "All mandatory fields have to be filled.",
      "badState": "We are unable to complete registration of this account. Please contact customer service.",
      "tryAgain": "Please try again",
      "contactCustomerService": "Please contact customer service"
    },
    "submit": "Confirm identity",
    "continueWithoutSignUp": "Continue without national ID Number",
    "kyc": {
      "header": "Verify your identity"
    },
    "description": "According to Norwegian law we need to verify your identity when you are using our services. If you are doing any changes to your subscription or shopping in the online store, you should be confident that your safety is taken care of."
  }
}
</i18n>

<template>
  <div class="page">
    <FormWrapper>
      <div>
        <TnHeading type="h1" size="m" align="center" class="margin-vertical-l">
          {{ t('kyc.header') }}
        </TnHeading>

        <TnNotificationInline
          v-if="error"
          horizontal
          status="critical"
          full-width
          class="margin-bottom-l"
        >
          <TnLink v-if="errorLink" :href="errorLink" :text="t(error)" />
          <span v-else>{{ t(error) }}</span>
        </TnNotificationInline>

        <div v-if="isValidIdps">
          <Idp v-for="idp in idps" :key="idp.displayName" :idp="idp" :url="getIdpUrl(idp)" />
        </div>

        <TnParagraph size="s" class="margin-top-l">{{ t('description') }}</TnParagraph>
      </div>
    </FormWrapper>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  TnHeading,
  TnLink,
  TnNotificationInline,
  TnParagraph,
} from '@telenornorgeinternal/telegrafen-vue3-core';

import FormWrapper from '@/components/FormWrapper.vue';
import IdpComponent from '@/components/Idp.vue';
import { StepUpService } from './stepup.service';
import { EnvironmentService } from '@/environment';
import { IdpService } from '@/views/ibis/login/idp.service';
import { ErrorService } from '@/error/error.service';
import { RequestParsingService } from '@/views/ibis/requestParsing.service';
import type { ApiError, Idp as IdpModel } from '@/types';

const Idp = IdpComponent;

const { t, locale } = useI18n();
const route = useRoute();

const environment = ref<Record<string, string>>({});
const idps = ref<IdpModel[]>([]);
const isValidIdps = ref(false);
const error = ref<string | null>(null);
const errorLink = ref('');
const request = ref<Record<string, unknown>>({});
const setupStepUpRequest = ref<Record<string, unknown>>({});

function handleError(apiError: ApiError) {
  error.value = apiError.i18nPath;

  if (apiError.hasLink) {
    errorLink.value = locale.value === 'no' ? environment.value.ksUrlNo : environment.value.ksUrlEn;
  }
}

function getIdpUrl(idp: IdpModel) {
  const idpRequest: Record<string, unknown> = { ...request.value };
  idpRequest.authScheme = idp.authScheme;
  idpRequest.idps = null;

  if (!idpRequest.authorizeParams) {
    idpRequest.authorizeParams = {};
  }

  if (idp.amr) {
    console.debug(`Setting AMR to ${idp.amr}`);
    (idpRequest.authorizeParams as Record<string, unknown>).amr = idp.amr;
  }

  return `/step-up/challenge?requestUrlEncoded=${RequestParsingService.toUrlEncodedJson(idpRequest)}`;
}

onMounted(async () => {
  environment.value = await EnvironmentService.getEnvironment();

  const stepUpRequestId = (route.query.request as string) ?? null;
  const errorCode = route.query.error;

  const response = (await new StepUpService().getStepUpRequest(
    stepUpRequestId,
  )) as unknown as Record<string, unknown>;

  idps.value = new IdpService().getIdps(['no.bankid.vipps']);

  if (errorCode && (response.errorCode === 50000 || !response.errorCode)) {
    handleError(ErrorService.getError('ibis', errorCode));
    return;
  }

  if (response.errorCode) {
    handleError(response as unknown as ApiError);
    return;
  }

  setupStepUpRequest.value = response;
  request.value = RequestParsingService.parseUrlEncodedJson(response.encodedQuery) ?? {};

  if (response.encodedQuery) {
    isValidIdps.value = true;
  }
});
</script>
